import * as layout from '../../../components/markLayout.module.css';
import { MarkButton } from '../../../components/MarkControls';
import React from 'react';
import { Link } from 'react-router-dom';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import DripStatusPill from '../DripStatusPill';
import MarkUrlList from '../../../components/MarkUrlList';
import type { DripDetail, SegmentTargetKind } from '../../../types';
import SegmentAudiencePreview from '../../../components/SegmentAudiencePreview';
type Props = {
    drip: DripDetail;
    onEditAudience: () => void;
    onEditSchedule: () => void;
    scheduleEditDisabled: boolean;
};
const EditButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    title?: string;
}> = ({ onClick, disabled, title }) => (
    <MarkButton type="button" onClick={onClick} disabled={disabled} title={title}>
        Edit
    </MarkButton>
);
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className={layout.summaryRow}>
        <P noDefaultMargin strong>
            {label}
        </P>
        <div>{children}</div>
    </div>
);
const RE_ENTRY_LABELS: Record<string, string> = {
    once_ever: 'Once ever',
    once_per_n_days: 'Once per N days',
    always: 'Always'
};
const formatSendWindow = (start: string | null, end: string | null): string => {
    if (start && end) return `${start}–${end} (recipient local time)`;
    return 'Channel defaults (email 08:00–21:00, SMS 10:00–20:00 local)';
};
const Overview: React.FC<Props> = ({
    drip,
    onEditAudience,
    onEditSchedule,
    scheduleEditDisabled
}) => {
    const version = drip.draft_version ?? drip.current_version;
    return (
        <div className={layout.stack}>
            <div
                style={{
                    marginBottom: '1.2rem'
                }}
                className={layout.panel}
            >
                <P
                    strong
                    noDefaultMargin
                    styles={{
                        marginBottom: '0.8rem'
                    }}
                >
                    Identity (immutable)
                </P>
                <Row label="Name">
                    <P noDefaultMargin>{drip.name}</P>
                </Row>
                <Row label="Slug">
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            fontFamily: 'monospace'
                        }}
                    >
                        {drip.slug}
                    </P>
                </Row>
                <Row label="Status">
                    <DripStatusPill status={drip.status} />
                </Row>
                <Row label="Feature gate">
                    <Tag type="primary" size="small">
                        {drip.feature_gate}
                    </Tag>
                </Row>
            </div>
            <div
                style={{
                    marginBottom: '1.2rem'
                }}
                className={layout.panel}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.8rem'
                    }}
                >
                    <P strong noDefaultMargin>
                        Trigger & audience
                    </P>
                    {drip.trigger_type === 'SEGMENT' && <EditButton onClick={onEditAudience} />}
                </div>
                <Row label="Trigger type">
                    <Tag type={drip.trigger_type === 'PROSPECT' ? 'warn' : 'primary'} size="small">
                        {drip.trigger_type}
                    </Tag>
                </Row>
                {drip.trigger_type === 'PROSPECT' && drip.prospect_type && (
                    <Row label="Prospect type">
                        <Tag type="warn" size="small">
                            {drip.prospect_type}
                        </Tag>
                    </Row>
                )}
                <Row label="Target kind">
                    <P noDefaultMargin>{drip.target_kind}</P>
                </Row>
                {drip.trigger_type === 'SEGMENT' && (
                    <Row label="Enrollment segment">
                        {drip.enrollment_segment_id != null ? (
                            <Link to={`/mark/segments/${drip.enrollment_segment_id}`}>
                                {drip.enrollment_segment_name ??
                                    `Segment ${drip.enrollment_segment_id}`}
                            </Link>
                        ) : (
                            <P noDefaultMargin>(none)</P>
                        )}
                        {drip.enrollment_segment_id != null && (
                            <div style={{ marginTop: '1.2rem' }}>
                                <SegmentAudiencePreview
                                    key={`${drip.drip_id}-${drip.enrollment_segment_id}`}
                                    segmentId={drip.enrollment_segment_id}
                                    segmentName={drip.enrollment_segment_name}
                                    targetKind={drip.target_kind as SegmentTargetKind}
                                    description="These are current segment matches. Enrollment also depends on suppression and the drip's re-entry policy."
                                />
                            </div>
                        )}
                    </Row>
                )}
                {drip.trigger_type === 'SEGMENT' && (
                    <Row label="Re-entry policy">
                        <P noDefaultMargin>
                            {RE_ENTRY_LABELS[drip.re_entry_policy] ?? drip.re_entry_policy}
                            {drip.re_entry_policy === 'once_per_n_days' &&
                                drip.re_entry_days != null &&
                                ` (${drip.re_entry_days} days)`}
                        </P>
                    </Row>
                )}
            </div>
            <div
                style={{
                    marginBottom: '1.2rem'
                }}
                className={layout.panel}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.8rem'
                    }}
                >
                    <P strong noDefaultMargin>
                        Send window
                    </P>
                    <EditButton
                        onClick={onEditSchedule}
                        disabled={scheduleEditDisabled}
                        title={
                            scheduleEditDisabled
                                ? 'Save or discard your step changes first.'
                                : undefined
                        }
                    />
                </div>
                <Row label="Window">
                    <P noDefaultMargin>
                        {formatSendWindow(
                            version?.send_window_start ?? null,
                            version?.send_window_end ?? null
                        )}
                    </P>
                </Row>
            </div>
            <div
                style={{
                    marginBottom: '1.2rem'
                }}
                className={layout.panel}
            >
                <P
                    strong
                    noDefaultMargin
                    styles={{
                        marginBottom: '0.8rem'
                    }}
                >
                    Stats snapshot
                </P>
                <div
                    style={{
                        display: 'flex',
                        gap: '1.6rem',
                        flexWrap: 'wrap'
                    }}
                >
                    <div
                        style={{
                            textAlign: 'center'
                        }}
                    >
                        <P
                            noDefaultMargin
                            strong
                            styles={{
                                fontSize: '1.8rem'
                            }}
                        >
                            {drip.stats.enrolled_total.toLocaleString()}
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Enrolled
                        </P>
                    </div>
                    <div
                        style={{
                            textAlign: 'center'
                        }}
                    >
                        <P
                            noDefaultMargin
                            strong
                            styles={{
                                fontSize: '1.8rem'
                            }}
                        >
                            {drip.stats.active.toLocaleString()}
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Active
                        </P>
                    </div>
                    <div
                        style={{
                            textAlign: 'center'
                        }}
                    >
                        <P
                            noDefaultMargin
                            strong
                            styles={{
                                fontSize: '1.8rem'
                            }}
                        >
                            {drip.stats.completed.toLocaleString()}
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Completed
                        </P>
                    </div>
                    <div
                        style={{
                            textAlign: 'center'
                        }}
                    >
                        <P
                            noDefaultMargin
                            strong
                            styles={{
                                fontSize: '1.8rem'
                            }}
                        >
                            {drip.stats.exited.toLocaleString()}
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Exited
                        </P>
                    </div>
                </div>
            </div>
            {drip.urls.length > 0 && (
                <div
                    style={{
                        marginBottom: '1.2rem'
                    }}
                    className={layout.panel}
                >
                    <P
                        strong
                        noDefaultMargin
                        styles={{
                            marginBottom: '0.8rem'
                        }}
                    >
                        URLs
                    </P>
                    <MarkUrlList urls={drip.urls} />
                </div>
            )}
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    marginTop: '0.4rem'
                }}
            >
                Created: {new Date(drip.created).toLocaleDateString()}
            </P>
        </div>
    );
};
export default Overview;
